import { observer } from 'mobx-react-lite'
import { CircularProgress, Typography } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { ProjectRequirements } from '../../model/project-builder'
import { ColorTokens, Palette, RadiusTokens } from 'src/feature/theme/model/Palette'

interface ProjectBuilderResultsProps {
  projectRequirements: ProjectRequirements
  selectedSkill?: string
  onSelectSkill: (skillName: string) => void
  isLoading?: boolean
}

export const ProjectBuilderResults = observer(({
  projectRequirements,
  selectedSkill,
  onSelectSkill,
  isLoading = false,
}: ProjectBuilderResultsProps) => {
  const { translation } = useLocalizationStore()
  const projectBuilderTranslation = translation.pages.projectBuilder

  return (
    <AppPaper sx={styles.mainContent}>
      <Typography variant='h2'>{projectBuilderTranslation.resultsTitle()}</Typography>
      <Typography variant='body1' sx={{ marginTop: '-15px', color: 'text.secondary' }}>{projectBuilderTranslation.clickSkillToSeeMatchedUsers()}</Typography>

      <AppBox sx={styles.skillsList}>
        {projectRequirements.skills.map(skill => (
          <AppPaper
            key={skill.skillName}
            sx={{
              ...styles.skillCard,
              ...(selectedSkill === skill.skillName && styles.skillCardSelected),
            }}
            onClick={() => onSelectSkill(skill.skillName)}
          >
            <AppBox
              sx={{
                ...styles.skillHeader,
                ...(selectedSkill === skill.skillName && styles.skillHeaderActive),
              }}
            >
              <Typography
                sx={{
                  ...styles.skillTitle,
                  ...(selectedSkill === skill.skillName && styles.skillTitleActive),
                }}
              >
                {skill.skillName}
              </Typography>

              <AppBox sx={{
                ...styles.priorityTag,
                ...(skill.importance === 'high' && styles.priorityHigh),
                ...(skill.importance === 'medium' && styles.priorityMedium),
                ...(skill.importance === 'low' && styles.priorityLow),
              }}>
                {projectBuilderTranslation[skill.importance as 'high' | 'medium' | 'low']()}
              </AppBox>
            </AppBox>

            <AppBox sx={styles.skillBody}>
              <AppBox sx={styles.skillContent}>
                <Typography sx={styles.skillDescription}>
                  {skill.skillDescription}
                </Typography>

                <Typography sx={styles.suggested}>
                  {projectBuilderTranslation.suggestedRating()}: {skill.suggestedRating}/5
                </Typography>

                <Typography sx={styles.suggested}>
                  {projectBuilderTranslation.suggestedNumberOfPeople()}: {skill.suggestedNumberOfPeople}
                </Typography>
              </AppBox>
            </AppBox>
            {selectedSkill === skill.skillName && isLoading ? (
              <CircularProgress size={18} sx={styles.loadingArrow} />
            ) : (
              <Typography sx={styles.arrow}>
                ›
              </Typography>
            )}
          </AppPaper>
        ))}
      </AppBox>
    </AppPaper>
  )
})

const styles = {
  mainContent: {
    borderRadius: '12px',
    padding: '10px 15px',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    border: 0,
    width: '100%',
  },
  skillsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    width: '100%',
  },
  skillCard: {
    position: 'relative',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    cursor: 'pointer',
    border: '1px solid',
    borderColor: 'transparent',
    backgroundColor: 'background.dark',
    transition: 'border-color 0.2s ease-in-out',
    '&:hover': {
      border: `1px solid ${ColorTokens.border100}`,
    },
  },
  skillHeader: {
    width: '100%',
    padding: '10px 15px',
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    alignItems: {
      xs: 'flex-start',
      md: 'center',
    },
    borderBottom: `1px solid ${ColorTokens.green10}`,
  },
  skillTitle: {
    color: 'text.primary',
    fontWeight: 600,
    fontSize: 14,
  },
  skillHeaderActive: {
    background: Palette.CORAL_REEF_GREEN,
    borderBottom: 'none',
  },
  skillTitleActive: {
    color: ColorTokens.neutral100,
  },
  priorityTag: {
    borderRadius: RadiusTokens.pill,
    minWidth: 110,
    padding: '5px 10px',
    fontSize: 12,
    lineHeight: 1.2,
    color: 'background.default',
    justifyContent: 'center',
  },
  priorityHigh: {
    backgroundColor: 'error.main',
  },
  priorityMedium: {
    backgroundColor: 'warning.main',
  },
  priorityLow: {
    backgroundColor: 'primary.main',
  },
  skillBody: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 15px',
    alignItems: 'center',
    minHeight: 80,
  },
  skillContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  skillDescription: {
    fontSize: 12,
    color: 'text.secondary',
  },
  suggested: {
    fontSize: 12,
    fontWeight: 500,
    color: 'text.secondary',
  },
  arrow: {
    position: 'absolute',
    right: '1rem',
    top: '67%',
    transform: 'translateY(-50%)',
    fontSize: 70,
    lineHeight: 1,
  },
  loadingArrow: {
    position: 'absolute',
    right: '1rem',
    top: '67%',
    transform: 'translateY(-50%)',
    color: 'text.secondary',
  },
  skillCardSelected: {
    backgroundColor: ColorTokens.primary10,
    border: `1px solid ${ColorTokens.primary400}`,
  },
}